import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import AdminLayout from "@/components/layout-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, TrendingDown, DollarSign, CalendarDays, Home,
  ArrowUpRight, ArrowDownRight, Minus, BarChart3, Target,
  Building2, Sparkles, Wrench, SlidersHorizontal, PiggyBank,
  Users, Percent, BedDouble, Calculator, Briefcase, HardHat,
  Lightbulb, Send, Loader2, X, ChevronUp, MessageSquare,
  Shield, Zap, Rocket, CheckCircle2
} from "lucide-react";

const MONTH_LABELS: Record<string, string> = {
  "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr",
  "05": "May", "06": "Jun", "07": "Jul", "08": "Aug",
  "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec",
};

type BusinessMode = "property" | "services";
type ScenarioType = "conservative" | "moderate" | "optimistic" | "custom";

interface ScenarioData {
  label: string;
  description: string;
  occupancyRate: number;
  avgNightlyRate: number;
  apartments: number;
  cleaningCostPerTurnover: number;
  maintenanceCostPerMonth: number;
  commissionRate: number;
  avgStayDuration: number;
  actions: string[];
}

interface ServicesScenarioData {
  label: string;
  description: string;
  jobMultiplier: number;
  priceAdjust: number;
  transportMultiplier: number;
  actions: string[];
}

const SERVICES_SCENARIOS: Record<Exclude<ScenarioType, "custom">, ServicesScenarioData> = {
  conservative: {
    label: "Conservative",
    description: "Maintain current client base with stable operations",
    jobMultiplier: 0.85,
    priceAdjust: 1.0,
    transportMultiplier: 1.1,
    actions: [
      "Retain all existing property manager clients with consistent service quality",
      "Build a 2-week cash reserve from current net income",
      "Standardize cleaning checklists to reduce callback/re-clean rates",
      "Negotiate better supply pricing with current vendors for 5% savings",
      "Track cleaner performance metrics to identify top performers for retention bonuses",
    ],
  },
  moderate: {
    label: "Moderate",
    description: "Grow client base with targeted outreach and efficiency gains",
    jobMultiplier: 1.0,
    priceAdjust: 1.05,
    transportMultiplier: 1.0,
    actions: [
      "Raise cleaning fees by 5% across all tiers to reflect market rates",
      "Onboard 2-3 new property manager clients through referrals or local networking",
      "Implement route optimization to reduce transport costs per job",
      "Add upsell services (deep clean, laundry, restocking) at $25-50/addon",
      "Create branded before/after photo reports to demonstrate value to clients",
      "Cross-train cleaners on maintenance basics to handle small repairs on-site",
    ],
  },
  optimistic: {
    label: "Optimistic",
    description: "Aggressive expansion with premium positioning and new revenue streams",
    jobMultiplier: 1.3,
    priceAdjust: 1.10,
    transportMultiplier: 1.2,
    actions: [
      "Expand to 30% more jobs by onboarding 5+ new property manager clients",
      "Raise all service fees by 10% and position as premium service provider",
      "Hire 2 additional cleaners to handle increased volume without quality loss",
      "Launch a maintenance subscription plan ($200-400/mo per property) for recurring revenue",
      "Build direct booking relationships with Airbnb hosts for cleaning-only contracts",
      "Invest in professional cleaning equipment to reduce per-job time by 15%",
      "Create a training program for new hires to scale without sacrificing quality",
      "Partner with local real estate agents for move-in/move-out cleaning referrals",
    ],
  },
};

const SCENARIO_META: Record<Exclude<ScenarioType, "custom">, { icon: typeof Shield; color: string; bgColor: string; badgeVariant: string }> = {
  conservative: { icon: Shield, color: "text-blue-600", bgColor: "bg-blue-50 dark:bg-blue-950/30", badgeVariant: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  moderate: { icon: Zap, color: "text-amber-600", bgColor: "bg-amber-50 dark:bg-amber-950/30", badgeVariant: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  optimistic: { icon: Rocket, color: "text-green-600", bgColor: "bg-green-50 dark:bg-green-950/30", badgeVariant: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
};

function formatCurrency(value: number | undefined | null): string {
  const v = Number(value) || 0;
  if (Math.abs(v) >= 1000) {
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatMonth(monthKey: string): string {
  const [year, month] = monthKey.split("-");
  return `${MONTH_LABELS[month] || month} ${year.slice(2)}`;
}

function ChangeIndicator({ value, label }: { value: number; label?: string }) {
  const num = parseFloat(String(value));
  if (num > 0) return (
    <span className="inline-flex items-center gap-0.5 text-sm font-medium text-green-600">
      <ArrowUpRight className="w-3.5 h-3.5" /> +{num.toFixed(1)}%{label ? ` ${label}` : ''}
    </span>
  );
  if (num < 0) return (
    <span className="inline-flex items-center gap-0.5 text-sm font-medium text-red-500">
      <ArrowDownRight className="w-3.5 h-3.5" /> {num.toFixed(1)}%{label ? ` ${label}` : ''}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-0.5 text-sm font-medium text-muted-foreground">
      <Minus className="w-3.5 h-3.5" /> 0%{label ? ` ${label}` : ''}
    </span>
  );
}

function MiniBarChart({ data, maxValue }: {
  data: { label: string; value: number; projected?: boolean }[];
  maxValue: number;
}) {
  return (
    <div className="flex items-end gap-1 h-44" data-testid="chart-monthly-revenue">
      {data.map((item, i) => {
        const height = maxValue > 0 ? Math.max(4, (item.value / maxValue) * 100) : 4;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
            <div className="w-full relative group">
              <div
                className={`w-full rounded-t transition-all ${item.projected
                  ? 'bg-primary/20 border-2 border-primary border-dashed'
                  : 'bg-primary/60'
                  }`}
                style={{ height: `${height}%`, minHeight: '4px' }}
                data-testid={`bar-month-${item.label}`}
              />
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-popover border rounded px-2 py-1 text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-md z-10">
                {formatCurrency(item.value)}{item.projected ? ' (projected)' : ''}
              </div>
            </div>
            <span className="text-[10px] text-muted-foreground truncate w-full text-center">{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}

interface PropertyInputs {
  apartments: number;
  occupancyRate: number;
  avgNightlyRate: number;
  cleaningCostPerTurnover: number;
  maintenanceCostPerMonth: number;
  commissionRate: number;
  avgStayDuration: number;
  forecastMonths: number;
}

interface ServicesInputs {
  studioJobsPerMonth: number;
  studioPricePerJob: number;
  studioCleanerPay: number;
  studioSupplyCost: number;
  oneBrJobsPerMonth: number;
  oneBrPricePerJob: number;
  oneBrCleanerPay: number;
  oneBrSupplyCost: number;
  twoBrJobsPerMonth: number;
  twoBrPricePerJob: number;
  twoBrCleanerPay: number;
  twoBrSupplyCost: number;
  threeBrJobsPerMonth: number;
  threeBrPricePerJob: number;
  threeBrCleanerPay: number;
  threeBrSupplyCost: number;
  maintenanceJobsPerMonth: number;
  avgMaintenancePrice: number;
  checkInsPerMonth: number;
  avgCheckInFee: number;
  transportCostPerMonth: number;
  forecastMonths: number;
}

function computePropertyProjection(inputs: PropertyInputs) {
  const {
    apartments, occupancyRate, avgNightlyRate,
    cleaningCostPerTurnover, maintenanceCostPerMonth,
    commissionRate, avgStayDuration, forecastMonths
  } = inputs;

  const months: {
    month: string; grossRevenue: number; cleaningCost: number;
    maintenanceCost: number; commissions: number; netRevenue: number;
    bookings: number; occupiedNights: number;
  }[] = [];

  const now = new Date();

  for (let i = 1; i <= forecastMonths; i++) {
    const futureDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const daysInMonth = new Date(futureDate.getFullYear(), futureDate.getMonth() + 1, 0).getDate();
    const monthKey = `${futureDate.getFullYear()}-${String(futureDate.getMonth() + 1).padStart(2, '0')}`;

    const totalNightsAvailable = apartments * daysInMonth;
    const occupiedNights = Math.round(totalNightsAvailable * (occupancyRate / 100));
    const bookings = avgStayDuration > 0 ? Math.round(occupiedNights / avgStayDuration) : 0;
    const grossRevenue = occupiedNights * avgNightlyRate;
    const cleaningCost = bookings * cleaningCostPerTurnover;
    const maintenanceCost = maintenanceCostPerMonth;
    const commissions = grossRevenue * (commissionRate / 100);
    const netRevenue = grossRevenue - cleaningCost - maintenanceCost - commissions;

    months.push({ month: monthKey, grossRevenue, cleaningCost, maintenanceCost, commissions, netRevenue, bookings, occupiedNights });
  }

  const totals = months.reduce((acc, m) => ({
    grossRevenue: acc.grossRevenue + m.grossRevenue,
    cleaningCost: acc.cleaningCost + m.cleaningCost,
    maintenanceCost: acc.maintenanceCost + m.maintenanceCost,
    commissions: acc.commissions + m.commissions,
    netRevenue: acc.netRevenue + m.netRevenue,
    bookings: acc.bookings + m.bookings,
    occupiedNights: acc.occupiedNights + m.occupiedNights,
  }), { grossRevenue: 0, cleaningCost: 0, maintenanceCost: 0, commissions: 0, netRevenue: 0, bookings: 0, occupiedNights: 0 });

  return { months, totals };
}

function computeServicesProjection(inputs: ServicesInputs) {
  const {
    studioJobsPerMonth, studioPricePerJob, studioCleanerPay, studioSupplyCost,
    oneBrJobsPerMonth, oneBrPricePerJob, oneBrCleanerPay, oneBrSupplyCost,
    twoBrJobsPerMonth, twoBrPricePerJob, twoBrCleanerPay, twoBrSupplyCost,
    threeBrJobsPerMonth, threeBrPricePerJob, threeBrCleanerPay, threeBrSupplyCost,
    maintenanceJobsPerMonth, avgMaintenancePrice,
    checkInsPerMonth, avgCheckInFee,
    transportCostPerMonth,
    forecastMonths
  } = inputs;

  const months: {
    month: string; cleaningIncome: number; cleanerPayroll: number; supplyCost: number;
    maintenanceIncome: number; checkInIncome: number; grossIncome: number;
    transportCost: number; totalCosts: number; netIncome: number;
    totalJobs: number;
  }[] = [];

  const now = new Date();
  const totalCleaningJobsPerMonth = studioJobsPerMonth + oneBrJobsPerMonth + twoBrJobsPerMonth + threeBrJobsPerMonth;

  for (let i = 1; i <= forecastMonths; i++) {
    const futureDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const monthKey = `${futureDate.getFullYear()}-${String(futureDate.getMonth() + 1).padStart(2, '0')}`;

    const cleaningIncome = (studioJobsPerMonth * studioPricePerJob) + (oneBrJobsPerMonth * oneBrPricePerJob)
      + (twoBrJobsPerMonth * twoBrPricePerJob) + (threeBrJobsPerMonth * threeBrPricePerJob);
    const cleanerPayroll = (studioJobsPerMonth * studioCleanerPay) + (oneBrJobsPerMonth * oneBrCleanerPay)
      + (twoBrJobsPerMonth * twoBrCleanerPay) + (threeBrJobsPerMonth * threeBrCleanerPay);
    const supplyCost = (studioJobsPerMonth * studioSupplyCost) + (oneBrJobsPerMonth * oneBrSupplyCost)
      + (twoBrJobsPerMonth * twoBrSupplyCost) + (threeBrJobsPerMonth * threeBrSupplyCost);
    const maintenanceIncome = maintenanceJobsPerMonth * avgMaintenancePrice;
    const checkInIncome = checkInsPerMonth * avgCheckInFee;
    const grossIncome = cleaningIncome + maintenanceIncome + checkInIncome;
    const transportCost = transportCostPerMonth;
    const totalCosts = cleanerPayroll + supplyCost + transportCost;
    const netIncome = grossIncome - totalCosts;
    const totalJobs = totalCleaningJobsPerMonth + maintenanceJobsPerMonth + checkInsPerMonth;

    months.push({
      month: monthKey, cleaningIncome, cleanerPayroll, supplyCost,
      maintenanceIncome, checkInIncome, grossIncome,
      transportCost, totalCosts, netIncome, totalJobs
    });
  }

  const totals = months.reduce((acc, m) => ({
    cleaningIncome: acc.cleaningIncome + m.cleaningIncome,
    cleanerPayroll: acc.cleanerPayroll + m.cleanerPayroll,
    supplyCost: acc.supplyCost + m.supplyCost,
    maintenanceIncome: acc.maintenanceIncome + m.maintenanceIncome,
    checkInIncome: acc.checkInIncome + m.checkInIncome,
    grossIncome: acc.grossIncome + m.grossIncome,
    transportCost: acc.transportCost + m.transportCost,
    totalCosts: acc.totalCosts + m.totalCosts,
    netIncome: acc.netIncome + m.netIncome,
    totalJobs: acc.totalJobs + m.totalJobs,
  }), {
    cleaningIncome: 0, cleanerPayroll: 0, supplyCost: 0,
    maintenanceIncome: 0, checkInIncome: 0, grossIncome: 0,
    transportCost: 0, totalCosts: 0, netIncome: 0, totalJobs: 0,
  });

  const tierBreakdown = [
    { type: "Studio", jobs: studioJobsPerMonth * forecastMonths, income: studioJobsPerMonth * studioPricePerJob * forecastMonths, pay: studioJobsPerMonth * studioCleanerPay * forecastMonths, supplies: studioJobsPerMonth * studioSupplyCost * forecastMonths },
    { type: "1 BR", jobs: oneBrJobsPerMonth * forecastMonths, income: oneBrJobsPerMonth * oneBrPricePerJob * forecastMonths, pay: oneBrJobsPerMonth * oneBrCleanerPay * forecastMonths, supplies: oneBrJobsPerMonth * oneBrSupplyCost * forecastMonths },
    { type: "2 BR", jobs: twoBrJobsPerMonth * forecastMonths, income: twoBrJobsPerMonth * twoBrPricePerJob * forecastMonths, pay: twoBrJobsPerMonth * twoBrCleanerPay * forecastMonths, supplies: twoBrJobsPerMonth * twoBrSupplyCost * forecastMonths },
    { type: "3 BR", jobs: threeBrJobsPerMonth * forecastMonths, income: threeBrJobsPerMonth * threeBrPricePerJob * forecastMonths, pay: threeBrJobsPerMonth * threeBrCleanerPay * forecastMonths, supplies: threeBrJobsPerMonth * threeBrSupplyCost * forecastMonths },
  ];

  return { months, totals, tierBreakdown };
}

function ScenarioSlider({ label, value, onChange, min, max, step, icon: Icon, prefix, suffix, testId }: {
  label: string; value: number; onChange: (v: number) => void;
  min: number; max: number; step: number;
  icon: any; prefix?: string; suffix?: string; testId: string;
}) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2 text-sm font-medium">
          <Icon className="w-4 h-4 text-muted-foreground" />
          {label}
        </Label>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={value}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
            }}
            className="w-20 h-8 text-right text-sm"
            min={min}
            max={max}
            step={step}
            data-testid={`input-${testId}`}
          />
          {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
        </div>
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        min={min}
        max={max}
        step={step}
        className="w-full"
        data-testid={`slider-${testId}`}
      />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{prefix}{min}{suffix}</span>
        <span>{prefix}{max}{suffix}</span>
      </div>
    </div>
  );
}

interface LumiChatMessage {
  role: "user" | "assistant";
  content: string;
}

function LumiForecastPanel({ businessMode, inputs, projections, forecastMonths }: {
  businessMode: BusinessMode;
  inputs: Record<string, any>;
  projections: Record<string, any>;
  forecastMonths: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<LumiChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const FORECAST_SUGGESTIONS = businessMode === "property"
    ? [
        "Is this forecast realistic based on my current data?",
        "What's the #1 lever to increase my net profit?",
        "How does my occupancy compare to market average?",
        "What happens if I add 2 more apartments?",
      ]
    : [
        "Which apartment tier has the best margins?",
        "Am I paying my cleaners too much or too little?",
        "How can I hit $10k/month net profit?",
        "What's the break-even number of jobs I need?",
      ];

  const sendMessage = useCallback(async (messageText?: string) => {
    const text = (messageText || input).trim();
    if (!text || isStreaming) return;

    const userMessage: LumiChatMessage = { role: "user", content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsStreaming(true);

    try {
      const response = await fetch("/api/admin/lumi/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: text,
          history: newMessages.slice(-8),
          forecastContext: { businessMode, inputs, projections, forecastMonths },
        }),
      });

      if (!response.ok) throw new Error("Failed to send message");
      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response stream");

      const decoder = new TextDecoder();
      let assistantContent = "";
      setMessages(prev => [...prev, { role: "assistant", content: "" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split("\n")) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                assistantContent += data.content;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: "assistant", content: assistantContent };
                  return updated;
                });
              }
              if (data.done) break;
            } catch {}
          }
        }
      }
    } catch {
      setMessages(prev => [
        ...prev.filter(m => m.role !== "assistant" || m.content !== ""),
        { role: "assistant", content: "Sorry, I had trouble processing that. Please try again." },
      ]);
    } finally {
      setIsStreaming(false);
    }
  }, [input, isStreaming, messages, businessMode, inputs, projections, forecastMonths]);

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all"
        data-testid="btn-open-lumi-forecast"
      >
        <Lightbulb className="w-5 h-5" />
        <span className="font-medium text-sm">Ask Lumi about this forecast</span>
        {messages.length > 0 && (
          <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold">
            {messages.filter(m => m.role === "assistant").length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 right-0 z-50 w-full sm:w-[420px] sm:right-6 sm:bottom-6 flex flex-col bg-background border rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[70vh]" data-testid="lumi-forecast-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/10 rounded-t-2xl">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
            <Lightbulb className="w-3.5 h-3.5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold">Lumi</h3>
            <p className="text-[10px] text-muted-foreground">Analyzing your {businessMode === "services" ? "services" : "property"} forecast</p>
          </div>
        </div>
        <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg hover:bg-black/5 transition-colors" data-testid="btn-close-lumi-forecast">
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[200px]">
        {messages.length === 0 && (
          <div className="space-y-3" data-testid="lumi-forecast-welcome">
            <p className="text-xs text-muted-foreground text-center py-2">
              I can see your current forecast scenario. Ask me anything about it.
            </p>
            <div className="space-y-1.5">
              {FORECAST_SUGGESTIONS.map((q, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(q)}
                  className="w-full text-left px-3 py-2 rounded-lg border bg-card hover:bg-accent hover:border-primary/20 transition-all text-xs text-muted-foreground hover:text-foreground flex items-start gap-2"
                  data-testid={`forecast-suggestion-${i}`}
                >
                  <Sparkles className="w-3 h-3 mt-0.5 text-amber-500 shrink-0" />
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[90%] rounded-xl px-3 py-2 ${msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
              {msg.role === "assistant" ? (
                <div className="prose prose-xs dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:text-xs [&_li]:text-xs [&_h1]:text-sm [&_h2]:text-xs [&_h3]:text-xs [&_table]:text-[10px] [&_th]:px-1.5 [&_td]:px-1.5">
                  <ReactMarkdown>{msg.content || "..."}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-xs whitespace-pre-wrap">{msg.content}</p>
              )}
            </div>
          </div>
        ))}

        {isStreaming && messages[messages.length - 1]?.role !== "assistant" && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-xl px-3 py-2">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" />
                Thinking...
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t px-3 py-2">
        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Ask about your forecast..."
            className="min-h-[36px] max-h-[80px] resize-none rounded-lg text-xs border-muted-foreground/20"
            rows={1}
            disabled={isStreaming}
            data-testid="input-lumi-forecast"
          />
          <Button
            onClick={() => sendMessage()}
            disabled={!input.trim() || isStreaming}
            size="icon"
            className="h-9 w-9 rounded-lg shrink-0 bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
            data-testid="btn-send-forecast"
          >
            {isStreaming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminForecast() {
  const [businessMode, setBusinessMode] = useState<BusinessMode>("property");
  const [activeScenario, setActiveScenario] = useState<ScenarioType>("moderate");

  const { data: forecast, isLoading } = useQuery<any>({
    queryKey: ["/api/admin/forecast"],
  });

  const baselines = forecast?.baselines;
  const scenarios: Record<string, ScenarioData> | undefined = forecast?.scenarios;

  const [propertyInputs, setPropertyInputs] = useState<PropertyInputs | null>(null);
  const [servicesInputs, setServicesInputs] = useState<ServicesInputs | null>(null);
  const [baselineServicesInputs, setBaselineServicesInputs] = useState<ServicesInputs | null>(null);

  const applyScenario = useCallback((scenarioKey: ScenarioType) => {
    setActiveScenario(scenarioKey);
    if (scenarioKey === "custom") return;

    if (businessMode === "property") {
      const s = scenarios?.[scenarioKey];
      if (!s) return;
      setPropertyInputs({
        apartments: s.apartments,
        occupancyRate: s.occupancyRate,
        avgNightlyRate: s.avgNightlyRate,
        cleaningCostPerTurnover: s.cleaningCostPerTurnover,
        maintenanceCostPerMonth: s.maintenanceCostPerMonth,
        commissionRate: s.commissionRate,
        avgStayDuration: s.avgStayDuration,
        forecastMonths: propertyInputs?.forecastMonths || 6,
      });
    }

    const base = baselineServicesInputs;
    if (base) {
      const svc = SERVICES_SCENARIOS[scenarioKey];
      setServicesInputs({
        ...base,
        studioJobsPerMonth: Math.max(1, Math.round(base.studioJobsPerMonth * svc.jobMultiplier)),
        studioPricePerJob: Math.round(base.studioPricePerJob * svc.priceAdjust),
        oneBrJobsPerMonth: Math.max(1, Math.round(base.oneBrJobsPerMonth * svc.jobMultiplier)),
        oneBrPricePerJob: Math.round(base.oneBrPricePerJob * svc.priceAdjust),
        twoBrJobsPerMonth: Math.max(1, Math.round(base.twoBrJobsPerMonth * svc.jobMultiplier)),
        twoBrPricePerJob: Math.round(base.twoBrPricePerJob * svc.priceAdjust),
        threeBrJobsPerMonth: Math.max(1, Math.round(base.threeBrJobsPerMonth * svc.jobMultiplier)),
        threeBrPricePerJob: Math.round(base.threeBrPricePerJob * svc.priceAdjust),
        maintenanceJobsPerMonth: Math.max(1, Math.round(base.maintenanceJobsPerMonth * svc.jobMultiplier)),
        checkInsPerMonth: Math.max(1, Math.round(base.checkInsPerMonth * svc.jobMultiplier)),
        transportCostPerMonth: Math.round(base.transportCostPerMonth * svc.transportMultiplier),
      });
    }
  }, [scenarios, propertyInputs?.forecastMonths, baselineServicesInputs, businessMode]);

  useEffect(() => {
    if (!propertyInputs && baselines && scenarios) {
      const moderateScenario = scenarios.moderate;
      if (moderateScenario) {
        setPropertyInputs({
          apartments: moderateScenario.apartments,
          occupancyRate: moderateScenario.occupancyRate,
          avgNightlyRate: moderateScenario.avgNightlyRate,
          cleaningCostPerTurnover: moderateScenario.cleaningCostPerTurnover,
          maintenanceCostPerMonth: moderateScenario.maintenanceCostPerMonth,
          commissionRate: moderateScenario.commissionRate,
          avgStayDuration: moderateScenario.avgStayDuration,
          forecastMonths: 6,
        });
        setActiveScenario("moderate");
      } else {
        setPropertyInputs({
          apartments: baselines.apartments || 4,
          occupancyRate: baselines.occupancyRate || 60,
          avgNightlyRate: baselines.avgNightlyRate || 100,
          cleaningCostPerTurnover: baselines.cleaningCostPerTurnover || 80,
          maintenanceCostPerMonth: baselines.maintenanceCostPerMonth || 200,
          commissionRate: baselines.commissionRate || 15,
          avgStayDuration: baselines.avgStayDuration || 3,
          forecastMonths: 6,
        });
      }
    }
  }, [baselines, propertyInputs, scenarios]);

  const { data: commissionSettings } = useQuery<any>({
    queryKey: ["/api/admin/finances/settings"],
  });

  useEffect(() => {
    if (!servicesInputs && baselines) {
      const avgBookingsPerMonth = Math.round((baselines.avgBookingsPerApartmentPerMonth || 2.5) * (baselines.apartments || 4));
      const s = commissionSettings || {};
      const initial: ServicesInputs = {
        studioJobsPerMonth: Math.max(1, Math.round(avgBookingsPerMonth * 0.1)),
        studioPricePerJob: Number(s.cleaningFeeStudio || 90),
        studioCleanerPay: Number(s.cleanerPayStudio || 60),
        studioSupplyCost: Number(s.supplyCostStudio || 8),
        oneBrJobsPerMonth: Math.max(1, Math.round(avgBookingsPerMonth * 0.3)),
        oneBrPricePerJob: Number(s.cleaningFee || 110),
        oneBrCleanerPay: Number(s.cleanerPay || 75),
        oneBrSupplyCost: Number(s.supplyCost || 10),
        twoBrJobsPerMonth: Math.max(1, Math.round(avgBookingsPerMonth * 0.4)),
        twoBrPricePerJob: Number(s.cleaningFee2br || 140),
        twoBrCleanerPay: Number(s.cleanerPay2br || 95),
        twoBrSupplyCost: Number(s.supplyCost2br || 15),
        threeBrJobsPerMonth: Math.max(1, Math.round(avgBookingsPerMonth * 0.2)),
        threeBrPricePerJob: Number(s.cleaningFee3br || 170),
        threeBrCleanerPay: Number(s.cleanerPay3br || 115),
        threeBrSupplyCost: Number(s.supplyCost3br || 20),
        maintenanceJobsPerMonth: 5,
        avgMaintenancePrice: 120,
        checkInsPerMonth: Math.max(1, avgBookingsPerMonth),
        avgCheckInFee: 50,
        transportCostPerMonth: 400,
        forecastMonths: 6,
      };
      setServicesInputs(initial);
      setBaselineServicesInputs(initial);
    }
  }, [baselines, servicesInputs, commissionSettings]);

  const activePropertyInputs: PropertyInputs = propertyInputs || {
    apartments: baselines?.apartments || 4,
    occupancyRate: baselines?.occupancyRate || 60,
    avgNightlyRate: baselines?.avgNightlyRate || 100,
    cleaningCostPerTurnover: baselines?.cleaningCostPerTurnover || 80,
    maintenanceCostPerMonth: baselines?.maintenanceCostPerMonth || 200,
    commissionRate: baselines?.commissionRate || 15,
    avgStayDuration: baselines?.avgStayDuration || 3,
    forecastMonths: 6,
  };

  const sDefaults = commissionSettings || {};
  const activeServicesInputs: ServicesInputs = servicesInputs || {
    studioJobsPerMonth: 1,
    studioPricePerJob: Number(sDefaults.cleaningFeeStudio || 90),
    studioCleanerPay: Number(sDefaults.cleanerPayStudio || 60),
    studioSupplyCost: 8,
    oneBrJobsPerMonth: 3,
    oneBrPricePerJob: Number(sDefaults.cleaningFee || 110),
    oneBrCleanerPay: Number(sDefaults.cleanerPay || 75),
    oneBrSupplyCost: 10,
    twoBrJobsPerMonth: 4,
    twoBrPricePerJob: Number(sDefaults.cleaningFee2br || 140),
    twoBrCleanerPay: Number(sDefaults.cleanerPay2br || 95),
    twoBrSupplyCost: 15,
    threeBrJobsPerMonth: 2,
    threeBrPricePerJob: Number(sDefaults.cleaningFee3br || 170),
    threeBrCleanerPay: Number(sDefaults.cleanerPay3br || 115),
    threeBrSupplyCost: 20,
    maintenanceJobsPerMonth: 5,
    avgMaintenancePrice: 120,
    checkInsPerMonth: 10,
    avgCheckInFee: 50,
    transportCostPerMonth: 400,
    forecastMonths: 6,
  };

  const updatePropertyInput = (key: keyof PropertyInputs, value: number) => {
    setActiveScenario("custom");
    setPropertyInputs(prev => ({ ...(prev || activePropertyInputs), [key]: value }));
  };

  const updateServicesInput = (key: keyof ServicesInputs, value: number) => {
    setActiveScenario("custom");
    setServicesInputs(prev => ({ ...(prev || activeServicesInputs), [key]: value }));
  };

  const propertyProjection = useMemo(() => computePropertyProjection(activePropertyInputs), [activePropertyInputs]);
  const servicesProjection = useMemo(() => computeServicesProjection(activeServicesInputs), [activeServicesInputs]);

  const historicalMonths = forecast?.historicalMonths || [];
  const topApartments = forecast?.topApartments || [];

  const forecastMonths = businessMode === "property" ? activePropertyInputs.forecastMonths : activeServicesInputs.forecastMonths;

  const combinedChartData = businessMode === "property"
    ? [
      ...historicalMonths.map((m: any) => ({ label: formatMonth(m.month), value: m.revenue || 0, projected: false })),
      ...propertyProjection.months.map(m => ({ label: formatMonth(m.month), value: m.grossRevenue, projected: true })),
    ]
    : [
      ...historicalMonths.map((m: any) => ({
        label: formatMonth(m.month),
        value: (m.cleaningCost || 0) + (m.maintenanceCost || 0),
        projected: false,
      })),
      ...servicesProjection.months.map(m => ({ label: formatMonth(m.month), value: m.grossIncome, projected: true })),
    ];
  const maxChartVal = Math.max(...combinedChartData.map((d: any) => d.value), 1);

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
          </div>
          <Skeleton className="h-64" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold" data-testid="text-forecast-title">Forecast & Analytics</h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">Data-driven scenarios with actionable recommendations</p>
          </div>

          <div className="flex rounded-xl bg-muted p-1 gap-1 w-full sm:w-auto" data-testid="toggle-business-mode">
            <button
              onClick={() => setBusinessMode("property")}
              className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all flex-1 sm:flex-initial min-w-0 ${businessMode === "property"
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
                }`}
              data-testid="btn-mode-property"
            >
              <Building2 className="w-4 h-4 shrink-0" />
              <span className="truncate">Property Mgmt</span>
            </button>
            <button
              onClick={() => setBusinessMode("services")}
              className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all flex-1 sm:flex-initial min-w-0 ${businessMode === "services"
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
                }`}
              data-testid="btn-mode-services"
            >
              <HardHat className="w-4 h-4 shrink-0" />
              <span className="truncate">Services</span>
            </button>
          </div>
        </div>

        {businessMode === "property" && (
          <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground flex items-start gap-2" data-testid="info-property-mode">
            <Building2 className="w-4 h-4 mt-0.5 shrink-0" />
            <span>You manage rental properties. Cleaning and maintenance are <strong className="text-foreground">costs</strong> that reduce your profit from bookings.</span>
          </div>
        )}
        {businessMode === "services" && (
          <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground flex items-start gap-2" data-testid="info-services-mode">
            <HardHat className="w-4 h-4 mt-0.5 shrink-0" />
            <span>You run a cleaning/maintenance company. Cleaning, maintenance, and check-ins are <strong className="text-foreground">income</strong> from services you provide.</span>
          </div>
        )}

        {((businessMode === "property" && scenarios) || (businessMode === "services" && baselineServicesInputs)) && (
          <div data-testid="scenario-cards-section">
            <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-muted-foreground" />
              {businessMode === "property" ? "Data-Driven Scenarios" : "Growth Scenarios"}
            </h2>
            <div className="grid gap-3 sm:grid-cols-3">
              {(["conservative", "moderate", "optimistic"] as const).map((key) => {
                const meta = SCENARIO_META[key];
                const Icon = meta.icon;
                const isActive = activeScenario === key;

                if (businessMode === "property") {
                  const scenario = scenarios![key];
                  if (!scenario) return null;
                  const projection = computePropertyProjection({
                    apartments: scenario.apartments,
                    occupancyRate: scenario.occupancyRate,
                    avgNightlyRate: scenario.avgNightlyRate,
                    cleaningCostPerTurnover: scenario.cleaningCostPerTurnover,
                    maintenanceCostPerMonth: scenario.maintenanceCostPerMonth,
                    commissionRate: scenario.commissionRate,
                    avgStayDuration: scenario.avgStayDuration,
                    forecastMonths: activePropertyInputs.forecastMonths,
                  });
                  return (
                    <Card
                      key={key}
                      className={`cursor-pointer transition-all border-2 ${isActive ? 'border-primary shadow-md' : 'border-transparent hover-elevate'}`}
                      onClick={() => applyScenario(key)}
                      data-testid={`scenario-card-${key}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`p-1.5 rounded-md ${meta.bgColor}`}>
                            <Icon className={`w-4 h-4 ${meta.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold">{scenario.label}</h3>
                            <p className="text-[10px] text-muted-foreground truncate">{scenario.description}</p>
                          </div>
                          {isActive && <Badge variant="secondary" className="text-[10px] shrink-0">Active</Badge>}
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-3">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-muted-foreground">Occupancy</span>
                            <span className="text-xs font-medium">{scenario.occupancyRate}%</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-muted-foreground">ADR</span>
                            <span className="text-xs font-medium">{formatCurrency(scenario.avgNightlyRate)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-muted-foreground">Units</span>
                            <span className="text-xs font-medium">{scenario.apartments}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-muted-foreground">Net/mo</span>
                            <span className={`text-xs font-bold ${projection.totals.netRevenue >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                              {formatCurrency(projection.totals.netRevenue / activePropertyInputs.forecastMonths)}
                            </span>
                          </div>
                        </div>
                        <div className="border-t pt-2">
                          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                            {activePropertyInputs.forecastMonths}mo projection
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Revenue</span>
                            <span className="text-xs font-semibold text-green-600">{formatCurrency(projection.totals.grossRevenue)}</span>
                          </div>
                          <div className="flex items-center justify-between mt-0.5">
                            <span className="text-xs text-muted-foreground">Net Profit</span>
                            <span className={`text-xs font-bold ${projection.totals.netRevenue >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                              {formatCurrency(projection.totals.netRevenue)}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                }

                const svcScenario = SERVICES_SCENARIOS[key];
                const base = baselineServicesInputs!;
                const svcInputs: ServicesInputs = {
                  ...base,
                  studioJobsPerMonth: Math.max(1, Math.round(base.studioJobsPerMonth * svcScenario.jobMultiplier)),
                  studioPricePerJob: Math.round(base.studioPricePerJob * svcScenario.priceAdjust),
                  oneBrJobsPerMonth: Math.max(1, Math.round(base.oneBrJobsPerMonth * svcScenario.jobMultiplier)),
                  oneBrPricePerJob: Math.round(base.oneBrPricePerJob * svcScenario.priceAdjust),
                  twoBrJobsPerMonth: Math.max(1, Math.round(base.twoBrJobsPerMonth * svcScenario.jobMultiplier)),
                  twoBrPricePerJob: Math.round(base.twoBrPricePerJob * svcScenario.priceAdjust),
                  threeBrJobsPerMonth: Math.max(1, Math.round(base.threeBrJobsPerMonth * svcScenario.jobMultiplier)),
                  threeBrPricePerJob: Math.round(base.threeBrPricePerJob * svcScenario.priceAdjust),
                  maintenanceJobsPerMonth: Math.max(1, Math.round(base.maintenanceJobsPerMonth * svcScenario.jobMultiplier)),
                  checkInsPerMonth: Math.max(1, Math.round(base.checkInsPerMonth * svcScenario.jobMultiplier)),
                  transportCostPerMonth: Math.round(base.transportCostPerMonth * svcScenario.transportMultiplier),
                };
                const svcProjection = computeServicesProjection(svcInputs);
                const totalJobs = svcInputs.studioJobsPerMonth + svcInputs.oneBrJobsPerMonth + svcInputs.twoBrJobsPerMonth + svcInputs.threeBrJobsPerMonth;

                return (
                  <Card
                    key={key}
                    className={`cursor-pointer transition-all border-2 ${isActive ? 'border-primary shadow-md' : 'border-transparent hover-elevate'}`}
                    onClick={() => applyScenario(key)}
                    data-testid={`scenario-card-${key}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`p-1.5 rounded-md ${meta.bgColor}`}>
                          <Icon className={`w-4 h-4 ${meta.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold">{svcScenario.label}</h3>
                          <p className="text-[10px] text-muted-foreground truncate">{svcScenario.description}</p>
                        </div>
                        {isActive && <Badge variant="secondary" className="text-[10px] shrink-0">Active</Badge>}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground">Jobs/mo</span>
                          <span className="text-xs font-medium">{totalJobs}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground">Fee adj.</span>
                          <span className="text-xs font-medium">{svcScenario.priceAdjust === 1 ? "Base" : `+${Math.round((svcScenario.priceAdjust - 1) * 100)}%`}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground">Income/mo</span>
                          <span className="text-xs font-medium text-green-600">
                            {formatCurrency(svcProjection.totals.grossIncome / svcInputs.forecastMonths)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground">Net/mo</span>
                          <span className={`text-xs font-bold ${svcProjection.totals.netIncome >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {formatCurrency(svcProjection.totals.netIncome / svcInputs.forecastMonths)}
                          </span>
                        </div>
                      </div>
                      <div className="border-t pt-2">
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                          {svcInputs.forecastMonths}mo projection
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Gross Income</span>
                          <span className="text-xs font-semibold text-green-600">{formatCurrency(svcProjection.totals.grossIncome)}</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-xs text-muted-foreground">Net Profit</span>
                          <span className={`text-xs font-bold ${svcProjection.totals.netIncome >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {formatCurrency(svcProjection.totals.netIncome)}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {activeScenario !== "custom" && (
              <Card className="mt-4 border-none shadow-sm" data-testid="scenario-actions-card">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <CheckCircle2 className={`w-5 h-5 ${SCENARIO_META[activeScenario].color}`} />
                    Action Plan &mdash; {businessMode === "property" ? scenarios?.[activeScenario]?.label : SERVICES_SCENARIOS[activeScenario].label} Scenario
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {businessMode === "property" ? scenarios?.[activeScenario]?.description : SERVICES_SCENARIOS[activeScenario].description}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(businessMode === "property"
                      ? (scenarios?.[activeScenario]?.actions || [])
                      : SERVICES_SCENARIOS[activeScenario].actions
                    ).map((action: string, i: number) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 p-2.5 rounded-md bg-muted/50"
                        data-testid={`action-item-${i}`}
                      >
                        <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${SCENARIO_META[activeScenario].badgeVariant}`}>
                          {i + 1}
                        </div>
                        <span className="text-xs leading-relaxed">{action}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-5">
          <Card className="lg:col-span-2 border-none shadow-sm overflow-hidden" data-testid="card-scenario-inputs">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-base flex-wrap">
                <SlidersHorizontal className="w-5 h-5 text-primary" />
                {businessMode === "property" ? "Property Scenario" : "Services Scenario"}
                {activeScenario !== "custom" && (
                  <Badge variant="secondary" className="text-[10px]">
                    {businessMode === "property" ? scenarios?.[activeScenario]?.label : SERVICES_SCENARIOS[activeScenario].label}
                  </Badge>
                )}
                {activeScenario === "custom" && (
                  <Badge variant="outline" className="text-[10px]">Custom</Badge>
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground">Adjust inputs to model your forecast</p>
            </CardHeader>
            <CardContent className="space-y-5">
              {businessMode === "property" ? (
                <>
                  <ScenarioSlider label="Apartments" value={activePropertyInputs.apartments} onChange={(v) => updatePropertyInput('apartments', v)} min={1} max={50} step={1} icon={Building2} suffix=" units" testId="scenario-apartments" />
                  <ScenarioSlider label="Occupancy Rate" value={activePropertyInputs.occupancyRate} onChange={(v) => updatePropertyInput('occupancyRate', v)} min={10} max={100} step={1} icon={Percent} suffix="%" testId="scenario-occupancy" />
                  <ScenarioSlider label="Avg Nightly Rate" value={activePropertyInputs.avgNightlyRate} onChange={(v) => updatePropertyInput('avgNightlyRate', v)} min={20} max={500} step={5} icon={DollarSign} prefix="$" testId="scenario-nightly-rate" />
                  <ScenarioSlider label="Avg Stay Duration" value={activePropertyInputs.avgStayDuration} onChange={(v) => updatePropertyInput('avgStayDuration', v)} min={1} max={30} step={0.5} icon={BedDouble} suffix=" nights" testId="scenario-stay-duration" />
                  <div className="border-t pt-4">
                    <p className="text-xs font-medium text-muted-foreground mb-4 uppercase tracking-wider">Costs</p>
                    <div className="space-y-5">
                      <ScenarioSlider label="Cleaning / Turnover" value={activePropertyInputs.cleaningCostPerTurnover} onChange={(v) => updatePropertyInput('cleaningCostPerTurnover', v)} min={0} max={300} step={5} icon={Sparkles} prefix="$" testId="scenario-cleaning-cost" />
                      <ScenarioSlider label="Maintenance / Month" value={activePropertyInputs.maintenanceCostPerMonth} onChange={(v) => updatePropertyInput('maintenanceCostPerMonth', v)} min={0} max={2000} step={25} icon={Wrench} prefix="$" testId="scenario-maintenance" />
                      <ScenarioSlider label="Commission Rate" value={activePropertyInputs.commissionRate} onChange={(v) => updatePropertyInput('commissionRate', v)} min={0} max={40} step={0.5} icon={Users} suffix="%" testId="scenario-commission" />
                    </div>
                  </div>
                  <ScenarioSlider label="Forecast Period" value={activePropertyInputs.forecastMonths} onChange={(v) => updatePropertyInput('forecastMonths', v)} min={3} max={24} step={1} icon={CalendarDays} suffix=" months" testId="scenario-period" />
                  <button
                    onClick={() => {
                      if (baselines) {
                        setPropertyInputs({
                          apartments: baselines.apartments || 4,
                          occupancyRate: baselines.occupancyRate || 60,
                          avgNightlyRate: baselines.avgNightlyRate || 100,
                          cleaningCostPerTurnover: baselines.cleaningCostPerTurnover || 80,
                          maintenanceCostPerMonth: baselines.maintenanceCostPerMonth || 200,
                          commissionRate: baselines.commissionRate || 15,
                          avgStayDuration: baselines.avgStayDuration || 3,
                          forecastMonths: 6,
                        });
                        setActiveScenario("custom");
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg text-sm font-medium bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                    data-testid="btn-reset-scenario"
                  >
                    Reset to Current Averages
                  </button>
                </>
              ) : (
                <>
                  <div data-testid="tier-pricing-table">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Cleaning Tiers</p>
                    <div className="overflow-x-auto -mx-1">
                      <table className="w-full min-w-[480px] text-sm">
                        <thead>
                          <tr className="text-[11px] text-muted-foreground uppercase tracking-wider">
                            <th className="text-left pb-2 pl-1 font-medium whitespace-nowrap">Type</th>
                            <th className="text-center pb-2 font-medium whitespace-nowrap">Jobs/mo</th>
                            <th className="text-center pb-2 font-medium whitespace-nowrap">Fee</th>
                            <th className="text-center pb-2 font-medium whitespace-nowrap">Pay</th>
                            <th className="text-center pb-2 font-medium whitespace-nowrap">Supply</th>
                            <th className="text-right pb-2 pr-1 font-medium whitespace-nowrap">Margin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {([
                            { label: "Studio", jobsKey: "studioJobsPerMonth" as const, feeKey: "studioPricePerJob" as const, payKey: "studioCleanerPay" as const, supplyKey: "studioSupplyCost" as const },
                            { label: "1 BR", jobsKey: "oneBrJobsPerMonth" as const, feeKey: "oneBrPricePerJob" as const, payKey: "oneBrCleanerPay" as const, supplyKey: "oneBrSupplyCost" as const },
                            { label: "2 BR", jobsKey: "twoBrJobsPerMonth" as const, feeKey: "twoBrPricePerJob" as const, payKey: "twoBrCleanerPay" as const, supplyKey: "twoBrSupplyCost" as const },
                            { label: "3 BR", jobsKey: "threeBrJobsPerMonth" as const, feeKey: "threeBrPricePerJob" as const, payKey: "threeBrCleanerPay" as const, supplyKey: "threeBrSupplyCost" as const },
                          ]).map((tier) => {
                            const fee = activeServicesInputs[tier.feeKey];
                            const pay = activeServicesInputs[tier.payKey];
                            const supply = activeServicesInputs[tier.supplyKey];
                            const margin = fee > 0 ? ((fee - pay - supply) / fee * 100) : 0;
                            return (
                              <tr key={tier.label} className="border-t" data-testid={`tier-row-${tier.label.toLowerCase().replace(' ', '')}`}>
                                <td className="py-2 pl-1 font-medium text-xs whitespace-nowrap">{tier.label}</td>
                                <td className="py-2 px-1">
                                  <Input type="number" value={activeServicesInputs[tier.jobsKey]} onChange={(e) => updateServicesInput(tier.jobsKey, Math.max(0, parseInt(e.target.value) || 0))} className="h-7 w-14 text-center text-xs mx-auto [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid={`input-${tier.jobsKey}`} />
                                </td>
                                <td className="py-2 px-1">
                                  <div className="flex items-center justify-center"><span className="text-[10px] text-muted-foreground mr-0.5">$</span><Input type="number" value={activeServicesInputs[tier.feeKey]} onChange={(e) => updateServicesInput(tier.feeKey, Math.max(0, parseFloat(e.target.value) || 0))} className="h-7 w-16 text-center text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid={`input-${tier.feeKey}`} /></div>
                                </td>
                                <td className="py-2 px-1">
                                  <div className="flex items-center justify-center"><span className="text-[10px] text-muted-foreground mr-0.5">$</span><Input type="number" value={activeServicesInputs[tier.payKey]} onChange={(e) => updateServicesInput(tier.payKey, Math.max(0, parseFloat(e.target.value) || 0))} className="h-7 w-16 text-center text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid={`input-${tier.payKey}`} /></div>
                                </td>
                                <td className="py-2 px-1">
                                  <div className="flex items-center justify-center"><span className="text-[10px] text-muted-foreground mr-0.5">$</span><Input type="number" value={activeServicesInputs[tier.supplyKey]} onChange={(e) => updateServicesInput(tier.supplyKey, Math.max(0, parseFloat(e.target.value) || 0))} className="h-7 w-14 text-center text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid={`input-${tier.supplyKey}`} /></div>
                                </td>
                                <td className={`py-2 pr-1 text-right text-xs font-semibold ${margin >= 30 ? 'text-green-600' : margin >= 15 ? 'text-amber-600' : 'text-red-500'}`}>
                                  {margin.toFixed(0)}%
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="border-t pt-4" data-testid="other-services-inputs">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Other Income & Costs</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-[11px] text-muted-foreground flex items-center gap-1"><Wrench className="w-3 h-3" /> Maint. jobs/mo</Label>
                        <Input type="number" value={activeServicesInputs.maintenanceJobsPerMonth} onChange={(e) => updateServicesInput('maintenanceJobsPerMonth', Math.max(0, parseInt(e.target.value) || 0))} className="h-8 text-sm" data-testid="input-maint-jobs" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px] text-muted-foreground flex items-center gap-1"><DollarSign className="w-3 h-3" /> Maint. price</Label>
                        <Input type="number" value={activeServicesInputs.avgMaintenancePrice} onChange={(e) => updateServicesInput('avgMaintenancePrice', Math.max(0, parseFloat(e.target.value) || 0))} className="h-8 text-sm" data-testid="input-maint-price" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px] text-muted-foreground flex items-center gap-1"><Home className="w-3 h-3" /> Check-ins/mo</Label>
                        <Input type="number" value={activeServicesInputs.checkInsPerMonth} onChange={(e) => updateServicesInput('checkInsPerMonth', Math.max(0, parseInt(e.target.value) || 0))} className="h-8 text-sm" data-testid="input-checkins" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px] text-muted-foreground flex items-center gap-1"><DollarSign className="w-3 h-3" /> Check-in fee</Label>
                        <Input type="number" value={activeServicesInputs.avgCheckInFee} onChange={(e) => updateServicesInput('avgCheckInFee', Math.max(0, parseFloat(e.target.value) || 0))} className="h-8 text-sm" data-testid="input-checkin-fee" />
                      </div>
                      <div className="space-y-1 col-span-2">
                        <Label className="text-[11px] text-muted-foreground flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Transport cost/mo</Label>
                        <Input type="number" value={activeServicesInputs.transportCostPerMonth} onChange={(e) => updateServicesInput('transportCostPerMonth', Math.max(0, parseFloat(e.target.value) || 0))} className="h-8 text-sm" data-testid="input-transport" />
                      </div>
                    </div>
                  </div>
                  <ScenarioSlider label="Forecast Period" value={activeServicesInputs.forecastMonths} onChange={(v) => updateServicesInput('forecastMonths', v)} min={3} max={24} step={1} icon={CalendarDays} suffix=" months" testId="scenario-period-svc" />
                  <button
                    onClick={() => {
                      const s = commissionSettings || {};
                      const avgBookingsPerMonth = Math.round((baselines?.avgBookingsPerApartmentPerMonth || 2.5) * (baselines?.apartments || 4));
                      setServicesInputs({
                        studioJobsPerMonth: Math.round(avgBookingsPerMonth * 0.1),
                        studioPricePerJob: Number(s.cleaningFeeStudio || 90),
                        studioCleanerPay: Number(s.cleanerPayStudio || 60),
                        studioSupplyCost: Number(s.supplyCostStudio || 8),
                        oneBrJobsPerMonth: Math.round(avgBookingsPerMonth * 0.3),
                        oneBrPricePerJob: Number(s.cleaningFee || 110),
                        oneBrCleanerPay: Number(s.cleanerPay || 75),
                        oneBrSupplyCost: Number(s.supplyCost || 10),
                        twoBrJobsPerMonth: Math.round(avgBookingsPerMonth * 0.4),
                        twoBrPricePerJob: Number(s.cleaningFee2br || 140),
                        twoBrCleanerPay: Number(s.cleanerPay2br || 95),
                        twoBrSupplyCost: Number(s.supplyCost2br || 15),
                        threeBrJobsPerMonth: Math.round(avgBookingsPerMonth * 0.2),
                        threeBrPricePerJob: Number(s.cleaningFee3br || 170),
                        threeBrCleanerPay: Number(s.cleanerPay3br || 115),
                        threeBrSupplyCost: Number(s.supplyCost3br || 20),
                        maintenanceJobsPerMonth: 5,
                        avgMaintenancePrice: 120,
                        checkInsPerMonth: avgBookingsPerMonth,
                        avgCheckInFee: 50,
                        transportCostPerMonth: 400,
                        forecastMonths: 6,
                      });
                    }}
                    className="w-full px-4 py-2 rounded-lg text-sm font-medium bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                    data-testid="btn-reset-scenario-svc"
                  >
                    Reset to Defaults
                  </button>
                </>
              )}
            </CardContent>
          </Card>

          <div className="lg:col-span-3 space-y-6">
            {businessMode === "property" ? (
              <>
                <div className="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4">
                  <Card className="border-none shadow-sm" data-testid="card-projected-gross">
                    <CardContent className="p-3 sm:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 sm:p-2 rounded-full bg-green-50 dark:bg-green-950/30">
                          <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-muted-foreground">Revenue</p>
                      </div>
                      <h3 className="text-lg sm:text-2xl font-bold" data-testid="text-projected-gross">
                        {formatCurrency(propertyProjection.totals.grossRevenue)}
                      </h3>
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">{activePropertyInputs.forecastMonths}mo</p>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm" data-testid="card-projected-net">
                    <CardContent className="p-3 sm:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 sm:p-2 rounded-full bg-emerald-50 dark:bg-emerald-950/30">
                          <PiggyBank className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-muted-foreground">Net Profit</p>
                      </div>
                      <h3 className={`text-lg sm:text-2xl font-bold ${propertyProjection.totals.netRevenue >= 0 ? '' : 'text-red-500'}`} data-testid="text-projected-net">
                        {formatCurrency(propertyProjection.totals.netRevenue)}
                      </h3>
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                        {propertyProjection.totals.grossRevenue > 0
                          ? `${(propertyProjection.totals.netRevenue / propertyProjection.totals.grossRevenue * 100).toFixed(1)}%`
                          : '0%'}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm" data-testid="card-projected-bookings">
                    <CardContent className="p-3 sm:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 sm:p-2 rounded-full bg-blue-50 dark:bg-blue-950/30">
                          <CalendarDays className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500" />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-muted-foreground">Bookings</p>
                      </div>
                      <h3 className="text-lg sm:text-2xl font-bold" data-testid="text-projected-bookings">
                        {propertyProjection.totals.bookings}
                      </h3>
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                        ~{Math.round(propertyProjection.totals.bookings / activePropertyInputs.forecastMonths)}/mo
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm" data-testid="card-projected-nights">
                    <CardContent className="p-3 sm:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 sm:p-2 rounded-full bg-purple-50 dark:bg-purple-950/30">
                          <BedDouble className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-500" />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-muted-foreground">Nights</p>
                      </div>
                      <h3 className="text-lg sm:text-2xl font-bold" data-testid="text-projected-nights">
                        {propertyProjection.totals.occupiedNights.toLocaleString()}
                      </h3>
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                        {activePropertyInputs.occupancyRate}% occ.
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <Card className="border-none shadow-sm" data-testid="card-projected-pnl">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Calculator className="w-5 h-5 text-muted-foreground" />
                      Projected P&L &mdash; Property Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-2 py-2">
                        <span className="text-xs sm:text-sm font-medium text-green-600 flex items-center gap-2 min-w-0">
                          <div className="w-3 h-3 rounded-full bg-green-500 shrink-0" />
                          <span className="truncate">Booking Revenue</span>
                        </span>
                        <span className="text-xs sm:text-sm font-bold text-green-600 shrink-0">{formatCurrency(propertyProjection.totals.grossRevenue)}</span>
                      </div>
                      <div className="border-t" />
                      <div className="flex items-center justify-between gap-2 py-1">
                        <span className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 min-w-0">
                          <div className="w-3 h-3 rounded-full bg-blue-400 shrink-0" />
                          <span className="truncate">Cleaning ({propertyProjection.totals.bookings} turnovers)</span>
                        </span>
                        <span className="text-xs sm:text-sm font-semibold text-red-500 shrink-0">-{formatCurrency(propertyProjection.totals.cleaningCost)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 py-1">
                        <span className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 min-w-0">
                          <div className="w-3 h-3 rounded-full bg-orange-400 shrink-0" />
                          <span className="truncate">Maintenance ({activePropertyInputs.forecastMonths}mo)</span>
                        </span>
                        <span className="text-xs sm:text-sm font-semibold text-red-500 shrink-0">-{formatCurrency(propertyProjection.totals.maintenanceCost)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 py-1">
                        <span className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 min-w-0">
                          <div className="w-3 h-3 rounded-full bg-amber-400 shrink-0" />
                          <span className="truncate">Commissions ({activePropertyInputs.commissionRate}%)</span>
                        </span>
                        <span className="text-xs sm:text-sm font-semibold text-red-500 shrink-0">-{formatCurrency(propertyProjection.totals.commissions)}</span>
                      </div>
                      <div className="border-t-2 border-primary/30" />
                      <div className="flex items-center justify-between py-2">
                        <span className="text-base font-bold flex items-center gap-2">
                          <PiggyBank className="w-4 h-4 text-primary" />
                          Net Profit
                        </span>
                        <span className={`text-lg font-bold ${propertyProjection.totals.netRevenue >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {formatCurrency(propertyProjection.totals.netRevenue)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                <div className="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4">
                  <Card className="border-none shadow-sm" data-testid="card-svc-gross">
                    <CardContent className="p-3 sm:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 sm:p-2 rounded-full bg-green-50 dark:bg-green-950/30">
                          <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-muted-foreground">Income</p>
                      </div>
                      <h3 className="text-lg sm:text-2xl font-bold" data-testid="text-svc-gross">
                        {formatCurrency(servicesProjection.totals.grossIncome)}
                      </h3>
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">{activeServicesInputs.forecastMonths}mo</p>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm" data-testid="card-svc-net">
                    <CardContent className="p-3 sm:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 sm:p-2 rounded-full bg-emerald-50 dark:bg-emerald-950/30">
                          <PiggyBank className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-muted-foreground">Net Profit</p>
                      </div>
                      <h3 className={`text-lg sm:text-2xl font-bold ${servicesProjection.totals.netIncome >= 0 ? '' : 'text-red-500'}`} data-testid="text-svc-net">
                        {formatCurrency(servicesProjection.totals.netIncome)}
                      </h3>
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                        {servicesProjection.totals.grossIncome > 0
                          ? `${(servicesProjection.totals.netIncome / servicesProjection.totals.grossIncome * 100).toFixed(1)}%`
                          : '0%'}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm" data-testid="card-svc-jobs">
                    <CardContent className="p-3 sm:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 sm:p-2 rounded-full bg-blue-50 dark:bg-blue-950/30">
                          <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500" />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-muted-foreground">Jobs</p>
                      </div>
                      <h3 className="text-lg sm:text-2xl font-bold" data-testid="text-svc-jobs">
                        {servicesProjection.totals.totalJobs}
                      </h3>
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                        ~{Math.round(servicesProjection.totals.totalJobs / activeServicesInputs.forecastMonths)}/mo
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm" data-testid="card-svc-costs">
                    <CardContent className="p-3 sm:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 sm:p-2 rounded-full bg-red-50 dark:bg-red-950/30">
                          <TrendingDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500" />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-muted-foreground">Costs</p>
                      </div>
                      <h3 className="text-lg sm:text-2xl font-bold text-red-500" data-testid="text-svc-costs">
                        {formatCurrency(servicesProjection.totals.totalCosts)}
                      </h3>
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                        {formatCurrency(servicesProjection.totals.totalCosts / activeServicesInputs.forecastMonths)}/mo
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <Card className="border-none shadow-sm" data-testid="card-svc-tier-breakdown">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BedDouble className="w-5 h-5 text-muted-foreground" />
                      Tier Breakdown ({activeServicesInputs.forecastMonths} Months)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[500px] text-sm" data-testid="table-tier-breakdown">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-2 font-medium text-muted-foreground whitespace-nowrap">Type</th>
                            <th className="pb-2 font-medium text-muted-foreground text-right whitespace-nowrap">Jobs</th>
                            <th className="pb-2 font-medium text-muted-foreground text-right whitespace-nowrap">Income</th>
                            <th className="pb-2 font-medium text-muted-foreground text-right whitespace-nowrap">Cleaner Pay</th>
                            <th className="pb-2 font-medium text-muted-foreground text-right whitespace-nowrap">Supplies</th>
                            <th className="pb-2 font-medium text-muted-foreground text-right whitespace-nowrap">Net</th>
                          </tr>
                        </thead>
                        <tbody>
                          {servicesProjection.tierBreakdown.map((tier: any) => (
                            <tr key={tier.type} className="border-b last:border-0">
                              <td className="py-2 font-medium">{tier.type}</td>
                              <td className="py-2 text-right">{tier.jobs}</td>
                              <td className="py-2 text-right text-green-600">{formatCurrency(tier.income)}</td>
                              <td className="py-2 text-right text-red-500">{formatCurrency(tier.pay)}</td>
                              <td className="py-2 text-right text-orange-500">{formatCurrency(tier.supplies)}</td>
                              <td className="py-2 text-right font-semibold">{formatCurrency(tier.income - tier.pay - tier.supplies)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 font-bold">
                            <td className="pt-3">Total</td>
                            <td className="pt-3 text-right">{servicesProjection.tierBreakdown.reduce((s: number, t: any) => s + t.jobs, 0)}</td>
                            <td className="pt-3 text-right text-green-600">{formatCurrency(servicesProjection.totals.cleaningIncome)}</td>
                            <td className="pt-3 text-right text-red-500">{formatCurrency(servicesProjection.totals.cleanerPayroll)}</td>
                            <td className="pt-3 text-right text-orange-500">{formatCurrency(servicesProjection.totals.supplyCost)}</td>
                            <td className="pt-3 text-right">{formatCurrency(servicesProjection.totals.cleaningIncome - servicesProjection.totals.cleanerPayroll - servicesProjection.totals.supplyCost)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-sm" data-testid="card-svc-pnl">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Calculator className="w-5 h-5 text-muted-foreground" />
                      Projected P&L &mdash; Services Business
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Income</p>
                      <div className="flex items-center justify-between gap-2 py-1">
                        <span className="text-xs sm:text-sm text-green-600 flex items-center gap-2 min-w-0">
                          <div className="w-3 h-3 rounded-full bg-blue-500 shrink-0" />
                          <span className="truncate">Cleaning ({servicesProjection.tierBreakdown.reduce((s: number, t: any) => s + t.jobs, 0)} jobs)</span>
                        </span>
                        <span className="text-xs sm:text-sm font-bold text-green-600 shrink-0">+{formatCurrency(servicesProjection.totals.cleaningIncome)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 py-1">
                        <span className="text-xs sm:text-sm text-green-600 flex items-center gap-2 min-w-0">
                          <div className="w-3 h-3 rounded-full bg-orange-500 shrink-0" />
                          <span className="truncate">Maintenance ({activeServicesInputs.maintenanceJobsPerMonth}/mo)</span>
                        </span>
                        <span className="text-xs sm:text-sm font-bold text-green-600 shrink-0">+{formatCurrency(servicesProjection.totals.maintenanceIncome)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 py-1">
                        <span className="text-xs sm:text-sm text-green-600 flex items-center gap-2 min-w-0">
                          <div className="w-3 h-3 rounded-full bg-purple-500 shrink-0" />
                          <span className="truncate">Check-ins ({activeServicesInputs.checkInsPerMonth}/mo)</span>
                        </span>
                        <span className="text-xs sm:text-sm font-bold text-green-600 shrink-0">+{formatCurrency(servicesProjection.totals.checkInIncome)}</span>
                      </div>
                      <div className="border-t flex items-center justify-between gap-2 py-2">
                        <span className="text-xs sm:text-sm font-semibold">Gross Income</span>
                        <span className="text-xs sm:text-sm font-bold text-green-600 shrink-0">{formatCurrency(servicesProjection.totals.grossIncome)}</span>
                      </div>

                      <div className="border-t pt-2" />
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Costs</p>
                      <div className="flex items-center justify-between gap-2 py-1">
                        <span className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 min-w-0">
                          <div className="w-3 h-3 rounded-full bg-red-400 shrink-0" />
                          <span className="truncate">Payroll (per-job)</span>
                        </span>
                        <span className="text-xs sm:text-sm font-semibold text-red-500 shrink-0">-{formatCurrency(servicesProjection.totals.cleanerPayroll)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 py-1">
                        <span className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 min-w-0">
                          <div className="w-3 h-3 rounded-full bg-red-300 shrink-0" />
                          <span className="truncate">Supplies</span>
                        </span>
                        <span className="text-xs sm:text-sm font-semibold text-red-500 shrink-0">-{formatCurrency(servicesProjection.totals.supplyCost)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 py-1">
                        <span className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 min-w-0">
                          <div className="w-3 h-3 rounded-full bg-red-200 shrink-0" />
                          <span className="truncate">Transport ({formatCurrency(activeServicesInputs.transportCostPerMonth)}/mo)</span>
                        </span>
                        <span className="text-xs sm:text-sm font-semibold text-red-500 shrink-0">-{formatCurrency(servicesProjection.totals.transportCost)}</span>
                      </div>

                      <div className="border-t-2 border-primary/30" />
                      <div className="flex items-center justify-between py-2">
                        <span className="text-base font-bold flex items-center gap-2">
                          <PiggyBank className="w-4 h-4 text-primary" />
                          Net Profit
                        </span>
                        <span className={`text-lg font-bold ${servicesProjection.totals.netIncome >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {formatCurrency(servicesProjection.totals.netIncome)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            <Card className="border-none shadow-sm" data-testid="card-monthly-chart">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="w-5 h-5 text-muted-foreground" />
                  {businessMode === "property" ? "Booking Revenue Timeline" : "Service Income Timeline"}
                </CardTitle>
                <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-2 rounded bg-primary/60" /> Historical
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-2 rounded bg-primary/20 border border-dashed border-primary" /> Projected
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                {combinedChartData.length > 0 ? (
                  <MiniBarChart data={combinedChartData} maxValue={maxChartVal} />
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No data yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card className="border-none shadow-sm" data-testid="card-monthly-breakdown">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Target className="w-5 h-5 text-muted-foreground" />
                Monthly Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                {businessMode === "property" ? (
                  <table className="w-full text-xs sm:text-sm" data-testid="table-monthly-projection">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-medium text-muted-foreground">Month</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Revenue</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Costs</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Profit</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Bkgs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {propertyProjection.months.map((m) => (
                        <tr key={m.month} className="border-b last:border-0" data-testid={`row-projection-${m.month}`}>
                          <td className="py-2 font-medium">{formatMonth(m.month)}</td>
                          <td className="py-2 text-right text-green-600">{formatCurrency(m.grossRevenue)}</td>
                          <td className="py-2 text-right text-red-500">
                            -{formatCurrency(m.cleaningCost + m.maintenanceCost + m.commissions)}
                          </td>
                          <td className={`py-2 text-right font-semibold ${m.netRevenue >= 0 ? '' : 'text-red-500'}`}>
                            {formatCurrency(m.netRevenue)}
                          </td>
                          <td className="py-2 text-right">{m.bookings}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className="w-full text-xs sm:text-sm" data-testid="table-monthly-svc-projection">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-medium text-muted-foreground">Month</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Income</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Costs</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Profit</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Jobs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {servicesProjection.months.map((m) => (
                        <tr key={m.month} className="border-b last:border-0" data-testid={`row-svc-${m.month}`}>
                          <td className="py-2 font-medium">{formatMonth(m.month)}</td>
                          <td className="py-2 text-right text-green-600">{formatCurrency(m.grossIncome)}</td>
                          <td className="py-2 text-right text-red-500">-{formatCurrency(m.totalCosts)}</td>
                          <td className={`py-2 text-right font-semibold ${m.netIncome >= 0 ? '' : 'text-red-500'}`}>
                            {formatCurrency(m.netIncome)}
                          </td>
                          <td className="py-2 text-right">{m.totalJobs}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            {businessMode === "property" && (
              <>
                <Card className="border-none shadow-sm" data-testid="card-historical-breakdown">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <DollarSign className="w-5 h-5 text-muted-foreground" />
                      Historical Summary (Last 12 Months)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[
                        { label: "Booking Revenue", key: "bookingRevenue", color: "bg-green-500" },
                        { label: "Cleaning Costs", key: "cleaningCost", color: "bg-blue-400" },
                        { label: "Commissions", key: "commissions", color: "bg-amber-400" },
                        { label: "Maintenance", key: "maintenanceCost", color: "bg-orange-400" },
                      ].map((item) => {
                        const total = historicalMonths.reduce((s: number, m: any) => s + (m[item.key] || 0), 0);
                        return (
                          <div key={item.key} className="flex items-center justify-between" data-testid={`hist-${item.key}`}>
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${item.color}`} />
                              <span className="text-sm">{item.label}</span>
                            </div>
                            <span className="text-sm font-semibold">{formatCurrency(total)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-sm" data-testid="card-top-apartments">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Building2 className="w-5 h-5 text-muted-foreground" />
                      Top Performing Apartments
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {topApartments.length > 0 ? (
                      <div className="space-y-3">
                        {topApartments.map((apt: any, index: number) => {
                          const maxTotal = topApartments[0]?.revenue || 1;
                          const barWidth = ((apt.revenue || 0) / maxTotal) * 100;
                          return (
                            <div key={apt.id} className="flex items-center gap-3" data-testid={`top-apt-${apt.id}`}>
                              <span className="text-sm font-medium text-muted-foreground w-6 shrink-0">{index + 1}.</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium truncate">{apt.name}</span>
                                  <span className="text-sm font-bold shrink-0 ml-2">{formatCurrency(apt.revenue || 0)}</span>
                                </div>
                                <div className="h-2 bg-muted rounded-full overflow-hidden">
                                  <div className="h-full bg-primary/60 rounded-full transition-all" style={{ width: `${barWidth}%` }} />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">No revenue data yet</p>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {businessMode === "services" && (
              <Card className="border-none shadow-sm" data-testid="card-svc-income-breakdown">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <DollarSign className="w-5 h-5 text-muted-foreground" />
                    Income Breakdown ({activeServicesInputs.forecastMonths} months)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { label: "Cleaning Income", value: servicesProjection.totals.cleaningIncome, color: "bg-blue-500", pct: servicesProjection.totals.grossIncome > 0 ? (servicesProjection.totals.cleaningIncome / servicesProjection.totals.grossIncome * 100) : 0 },
                      { label: "Maintenance Income", value: servicesProjection.totals.maintenanceIncome, color: "bg-orange-500", pct: servicesProjection.totals.grossIncome > 0 ? (servicesProjection.totals.maintenanceIncome / servicesProjection.totals.grossIncome * 100) : 0 },
                      { label: "Check-in Fees", value: servicesProjection.totals.checkInIncome, color: "bg-purple-500", pct: servicesProjection.totals.grossIncome > 0 ? (servicesProjection.totals.checkInIncome / servicesProjection.totals.grossIncome * 100) : 0 },
                    ].map((item) => (
                      <div key={item.label} data-testid={`svc-breakdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${item.color}`} />
                            <span className="text-sm">{item.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{item.pct.toFixed(0)}%</span>
                            <span className="text-sm font-semibold">{formatCurrency(item.value)}</span>
                          </div>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full ${item.color} rounded-full transition-all`} style={{ width: `${item.pct}%` }} />
                        </div>
                      </div>
                    ))}
                    <div className="border-t pt-3 flex items-center justify-between">
                      <span className="text-sm font-semibold">Total Service Income</span>
                      <span className="text-sm font-bold text-green-600">{formatCurrency(servicesProjection.totals.grossIncome)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">Total Operating Costs</span>
                      <span className="text-sm font-bold text-red-500">-{formatCurrency(servicesProjection.totals.totalCosts)}</span>
                    </div>
                    <div className="border-t pt-3 flex items-center justify-between">
                      <span className="text-base font-bold">Net Profit</span>
                      <span className={`text-base font-bold ${servicesProjection.totals.netIncome >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {formatCurrency(servicesProjection.totals.netIncome)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

        <LumiForecastPanel
          businessMode={businessMode}
          inputs={businessMode === "property"
            ? {
                apartments: activePropertyInputs.apartments,
                occupancyRate: `${activePropertyInputs.occupancyRate}%`,
                avgNightlyRate: `$${activePropertyInputs.avgNightlyRate}`,
                avgStayDuration: `${activePropertyInputs.avgStayDuration} nights`,
                cleaningCostPerTurnover: `$${activePropertyInputs.cleaningCostPerTurnover}`,
                maintenanceCostPerMonth: `$${activePropertyInputs.maintenanceCostPerMonth}`,
                commissionRate: `${activePropertyInputs.commissionRate}%`,
              }
            : {
                studioJobs: `${activeServicesInputs.studioJobsPerMonth}/mo at $${activeServicesInputs.studioPricePerJob} (pay $${activeServicesInputs.studioCleanerPay}, supply $${activeServicesInputs.studioSupplyCost})`,
                oneBrJobs: `${activeServicesInputs.oneBrJobsPerMonth}/mo at $${activeServicesInputs.oneBrPricePerJob} (pay $${activeServicesInputs.oneBrCleanerPay}, supply $${activeServicesInputs.oneBrSupplyCost})`,
                twoBrJobs: `${activeServicesInputs.twoBrJobsPerMonth}/mo at $${activeServicesInputs.twoBrPricePerJob} (pay $${activeServicesInputs.twoBrCleanerPay}, supply $${activeServicesInputs.twoBrSupplyCost})`,
                threeBrJobs: `${activeServicesInputs.threeBrJobsPerMonth}/mo at $${activeServicesInputs.threeBrPricePerJob} (pay $${activeServicesInputs.threeBrCleanerPay}, supply $${activeServicesInputs.threeBrSupplyCost})`,
                maintenanceJobs: `${activeServicesInputs.maintenanceJobsPerMonth}/mo at $${activeServicesInputs.avgMaintenancePrice}`,
                checkIns: `${activeServicesInputs.checkInsPerMonth}/mo at $${activeServicesInputs.avgCheckInFee}`,
                transportCost: `$${activeServicesInputs.transportCostPerMonth}/mo`,
              }
          }
          projections={businessMode === "property"
            ? {
                grossRevenue: propertyProjection.totals.grossRevenue,
                cleaningCost: propertyProjection.totals.cleaningCost,
                maintenanceCost: propertyProjection.totals.maintenanceCost,
                commissions: propertyProjection.totals.commissions,
                netProfit: propertyProjection.totals.netRevenue,
                totalBookings: propertyProjection.totals.bookings,
                occupiedNights: propertyProjection.totals.occupiedNights,
                profitMargin: propertyProjection.totals.grossRevenue > 0 ? (propertyProjection.totals.netRevenue / propertyProjection.totals.grossRevenue * 100) : 0,
              }
            : {
                grossServiceIncome: servicesProjection.totals.grossIncome,
                cleaningIncome: servicesProjection.totals.cleaningIncome,
                cleanerPayroll: servicesProjection.totals.cleanerPayroll,
                supplyCost: servicesProjection.totals.supplyCost,
                transportCost: servicesProjection.totals.transportCost,
                netProfit: servicesProjection.totals.netIncome,
                totalJobs: servicesProjection.totals.totalJobs,
                profitMargin: servicesProjection.totals.grossIncome > 0 ? (servicesProjection.totals.netIncome / servicesProjection.totals.grossIncome * 100) : 0,
              }
          }
          forecastMonths={forecastMonths}
        />
    </AdminLayout>
  );
}
